import { useState, useRef } from 'react';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { ClipboardList, CalendarClock, Check } from 'lucide-react';

const daysSince = (iso) => {
    if (!iso) return null;
    const d = Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);
    return Number.isFinite(d) && d >= 0 ? d : null;
};

/**
 * Sous-étapes structurées d'un chantier (Lot 4).
 * - ch_dossier_etude : 1) en cours Valnexo, 2) transmis (+date), 3) étude prête (+date)
 * - ch_planification : 1) depuis quelle date (+durée), 2) commande machines (date/délai/réception), 3) date chantier validé
 * Persiste l'objet `substeps` complet via onSave.
 */
export default function ChantierSubSteps({ stage, substeps, onSave, readOnly = false }) {
    const [data, setData] = useState(substeps || {});
    const saveTimer = useRef(null);

    const persist = (next) => {
        setData(next);
        if (!onSave) return;
        // Debounce : regroupe les saisies rapides (ex. champ "délai") en un seul enregistrement
        if (saveTimer.current) clearTimeout(saveTimer.current);
        saveTimer.current = setTimeout(() => {
            Promise.resolve(onSave(next)).catch(() => {});
        }, 500);
    };

    const updateSection = (section, patch) => {
        const next = { ...data, [section]: { ...(data[section] || {}), ...patch } };
        persist(next);
    };

    if (stage !== 'ch_dossier_etude' && stage !== 'ch_planification') return null;

    if (stage === 'ch_dossier_etude') {
        const s = data.dossier_etude || {};
        return (
            <div className="rounded-lg border border-purple-200 bg-purple-50/40 p-4 space-y-3" data-testid="substeps-dossier-etude">
                <h4 className="text-sm font-semibold text-purple-800 flex items-center gap-2">
                    <ClipboardList className="w-4 h-4" /> Dossier à l'étude — sous-étapes
                </h4>

                {/* 1. En cours de traitement Valnexo */}
                <label className="flex items-center gap-2 text-sm cursor-pointer" data-testid="substep-valnexo-en-cours">
                    <Checkbox checked={!!s.valnexo_en_cours} disabled={readOnly}
                        onCheckedChange={(v) => updateSection('dossier_etude', { valnexo_en_cours: !!v })} />
                    <span className="text-slate-700">1. En cours de traitement Valnexo</span>
                    {s.valnexo_en_cours && <Check className="w-3.5 h-3.5 text-emerald-600" />}
                </label>

                {/* 2. Transmis pour traitement (+date) */}
                <div className="space-y-1">
                    <label className="flex items-center gap-2 text-sm cursor-pointer" data-testid="substep-transmis">
                        <Checkbox checked={!!s.transmis} disabled={readOnly}
                            onCheckedChange={(v) => updateSection('dossier_etude', { transmis: !!v })} />
                        <span className="text-slate-700">2. Transmis pour traitement</span>
                    </label>
                    {s.transmis && (
                        <div className="ml-6 flex items-center gap-2">
                            <Label className="text-xs text-slate-500">Date de transmission</Label>
                            <Input type="date" value={s.transmis_date || ''} disabled={readOnly}
                                data-testid="substep-transmis-date"
                                onChange={(e) => updateSection('dossier_etude', { transmis_date: e.target.value })}
                                className="h-8 w-40 text-xs" />
                        </div>
                    )}
                </div>

                {/* 3. Étude prête à présenter (+date) */}
                <div className="space-y-1">
                    <label className="flex items-center gap-2 text-sm cursor-pointer" data-testid="substep-etude-prete">
                        <Checkbox checked={!!s.etude_prete} disabled={readOnly}
                            onCheckedChange={(v) => updateSection('dossier_etude', { etude_prete: !!v })} />
                        <span className="text-slate-700">3. Étude prête à être présentée</span>
                    </label>
                    {s.etude_prete && (
                        <div className="ml-6 flex items-center gap-2">
                            <Label className="text-xs text-slate-500">Date</Label>
                            <Input type="date" value={s.etude_prete_date || ''} disabled={readOnly}
                                data-testid="substep-etude-prete-date"
                                onChange={(e) => updateSection('dossier_etude', { etude_prete_date: e.target.value })}
                                className="h-8 w-40 text-xs" />
                        </div>
                    )}
                </div>
            </div>
        );
    }

    // ch_planification
    const p = data.planification || {};
    const dur = daysSince(p.depuis_date);
    return (
        <div className="rounded-lg border border-amber-200 bg-amber-50/40 p-4 space-y-3" data-testid="substeps-planification">
            <h4 className="text-sm font-semibold text-amber-800 flex items-center gap-2">
                <CalendarClock className="w-4 h-4" /> Planification du chantier — sous-étapes
            </h4>

            {/* 1. Depuis quelle date (+durée) */}
            <div className="flex items-center gap-2 flex-wrap">
                <Label className="text-sm text-slate-700 w-48">1. En planification depuis le</Label>
                <Input type="date" value={p.depuis_date || ''} disabled={readOnly}
                    data-testid="substep-planif-depuis-date"
                    onChange={(e) => updateSection('planification', { depuis_date: e.target.value })}
                    className="h-8 w-40 text-xs" />
                {dur != null && <Badge variant="outline" className="text-xs text-slate-600">depuis {dur} j</Badge>}
            </div>

            {/* 2. Commande des machines (date, délai, réception) */}
            <div className="space-y-2 border-t border-amber-200/60 pt-2">
                <p className="text-sm font-medium text-slate-700">2. Commande des machines</p>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 ml-2">
                    <div className="space-y-1">
                        <Label className="text-xs text-slate-500">Date de commande</Label>
                        <Input type="date" value={p.commande_date || ''} disabled={readOnly}
                            data-testid="substep-commande-date"
                            onChange={(e) => updateSection('planification', { commande_date: e.target.value })}
                            className="h-8 text-xs" />
                    </div>
                    <div className="space-y-1">
                        <Label className="text-xs text-slate-500">Délai annoncé</Label>
                        <Input type="text" value={p.commande_delai || ''} disabled={readOnly}
                            placeholder="ex : 4 semaines"
                            data-testid="substep-commande-delai"
                            onChange={(e) => updateSection('planification', { commande_delai: e.target.value })}
                            className="h-8 text-xs" />
                    </div>
                    <div className="space-y-1">
                        <Label className="text-xs text-slate-500">Date de réception</Label>
                        <Input type="date" value={p.commande_reception_date || ''} disabled={readOnly}
                            data-testid="substep-commande-reception"
                            onChange={(e) => updateSection('planification', { commande_reception_date: e.target.value })}
                            className="h-8 text-xs" />
                    </div>
                </div>
            </div>

            {/* 3. Date de chantier validé */}
            <div className="flex items-center gap-2 flex-wrap border-t border-amber-200/60 pt-2">
                <Label className="text-sm text-slate-700 w-48">3. Date de chantier validé</Label>
                <Input type="date" value={p.chantier_valide_date || ''} disabled={readOnly}
                    data-testid="substep-chantier-valide-date"
                    onChange={(e) => updateSection('planification', { chantier_valide_date: e.target.value })}
                    className="h-8 w-40 text-xs" />
                {p.chantier_valide_date && <Check className="w-4 h-4 text-emerald-600" />}
            </div>
        </div>
    );
}
